import { Component } from '@theme/component';

/** Continuous left-to-right announcement ribbon, rebuilt when its viewport changes. */
class AyomiAnnouncementMarquee extends Component {
  #observer;
  #abort;
  #frame;
  #generation = 0;
  #signature = '';

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.removeAttribute('data-paused');
    this.setAttribute('data-motion-enabled', '');
    this.querySelector('button')?.addEventListener('click', () => {
      const paused = this.toggleAttribute('data-paused');
      if (!paused) this.setAttribute('data-motion-enabled', '');
      const button = this.querySelector('button');
      button.setAttribute('aria-pressed', String(paused));
      button.querySelector('path').setAttribute('d', paused ? 'M5 3l7 5-7 5z' : 'M5 3v10M11 3v10');
      button.setAttribute('aria-label', paused ? 'Anons akışını sürdür' : 'Anons akışını duraklat');
    }, { signal });
    this.#observer = new ResizeObserver(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#build());
    });
    this.#observer.observe(this.querySelector('.ayomi-announcements__viewport'));
    this.#build();
    const generation = ++this.#generation;
    document.fonts.ready.then(() => {
      if (this.isConnected && generation === this.#generation) this.#build();
    });
  }

  #copy(node) {
    const copy = node.cloneNode(true);
    copy.removeAttribute('data-original');
    copy.setAttribute('data-copy', '');
    copy.setAttribute('aria-hidden', 'true');
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      element.removeAttribute('id');
      element.removeAttribute('data-shopify-editor-block');
      if (element.matches('a, button, [tabindex]')) element.setAttribute('tabindex', '-1');
    }
    return copy;
  }

  #build() {
    const viewport = this.querySelector('.ayomi-announcements__viewport');
    const track = this.querySelector('.ayomi-announcements__track');
    const original = this.querySelector('[data-original]');
    if (!original || !viewport || !track) return;
    // AYOMI 8 Ekim: Olculer degismediyse yeniden kurma. Font yuklenince ya da
    // ResizeObserver tetiklenince serit bastan baslayip ziplamasin.
    const ownItems = [...original.children].filter(node => !node.hasAttribute('data-copy'));
    const ownWidth = ownItems.reduce((sum, node) => sum + node.getBoundingClientRect().width, 0);
    const signature = `${Math.round(ownWidth)}|${viewport.clientWidth}`;
    if (signature === this.#signature && this.hasAttribute('data-ready')) return;
    this.#signature = signature;
    this.removeAttribute('data-ready');
    this.querySelectorAll('[data-copy]').forEach(node => node.remove());
    const items = [...original.children];
    if (!items.length) return;
    const baseWidth = original.getBoundingClientRect().width;
    if (!baseWidth) return;
    // One complete cycle is at least as wide as the viewport, even with a single announcement.
    const repeats = Math.max(1, Math.ceil(viewport.clientWidth / baseWidth));
    for (let i = 1; i < repeats; i++) {
      for (const item of items) original.append(this.#copy(item));
    }
    const width = baseWidth * repeats;
    track.prepend(this.#copy(original));
    this.style.setProperty('--ayomi-flow-width', `${width}px`);
    // Preserve the editor's speed setting: larger values produce a slower, gentler ribbon.
    const pixelsPerSecond = 150 / (Number(this.dataset.speed) || 5);
    this.style.setProperty('--ayomi-flow-duration', `${width / pixelsPerSecond}s`);
    this.setAttribute('data-ready', '');
    this.querySelector('button').hidden = false;
  }

  disconnectedCallback() {
    this.#generation++;
    this.#observer?.disconnect();
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    super.disconnectedCallback();
  }
}

if (!customElements.get('ayomi-announcement-marquee')) {
  customElements.define('ayomi-announcement-marquee', AyomiAnnouncementMarquee);
}
